import React from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import { LogoFullWhite, OwlLogo } from './svg';
import { BodyTextS } from './text';
import { BlackNavigationLink, ExternalLink, ExternalLinkClassNames } from '../components/link';
import { PAGES_INFO, TEMPLATES_INFO, MEDIA_INFO } from '../utils/pages-info';
import { WHITE } from '../utils/color-constants';
import createTranslate from '../utils/translator-provider';
import { openCookieConsent } from '$Spotti/utils/cookie-consent';

const infoColStyle = 'mb-12 flex flex-col items-start';

const Footer = () => {
  const data = useStaticQuery(graphql`
    query {
      translations: allContentfulTranslation {
        nodes {
          key
          value {
            value
          }
        }
      }

      campaigns: allContentfulCampaign {
        nodes {
          id
        }
      }
    }
  `);

  const translate = createTranslate(data.translations.nodes);

  return (
    <footer className="w-full flex flex-col items-center bg-white">
      <div className="w-full flex flex-col items-center bg-black-midnight">
        <div className="w-full lg:max-w-1200px flex flex-col">
          <div className="w-24 mt-6">
            <LogoFullWhite />
          </div>
          <div className="mt-6 mb-10 ml-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            <FooterInfoCol1 translate={translate} />
            <FooterInfoCol2 translate={translate} />
            <FooterInfoCol3
              translate={translate}
              campaigns={data.campaigns.nodes}
            />
            <FooterInfoCol4 translate={translate} />
          </div>
        </div>
      </div>
      <div className="w-10 mt-8">
        <OwlLogo />
      </div>
      <FooterCompanyInfoRow translate={translate} />
    </footer>
  );
};

const FooterInfoCol1 = ({ translate }) => {
  return (
    <div className={infoColStyle}>
      <BodyTextS color={WHITE}>
        {translate('footer.text.companyName')}
      </BodyTextS>
      <BodyTextS color={WHITE} className={'mt-4'}>
        {translate('footer.text.address')}
      </BodyTextS>
      <BodyTextS color={WHITE} className={'mt-4'}>
        {translate('footer.text.postNumber')}
      </BodyTextS>
      <BodyTextS color={WHITE} className={'mt-16'}>
        {translate('footer.text.phoneName')}
      </BodyTextS>
      <BodyTextS color={WHITE} className={'mt-4'}>
        {translate('footer.text.phoneNumber')}
      </BodyTextS>
    </div>
  );
};

const FooterInfoCol2 = ({ translate }) => {
  return (
    <div className={infoColStyle}>
      <BlackNavigationLink
        to={PAGES_INFO.advertiserLanding.path}
        className={'text-white font-extra-bold leading-loose'}
      >
        {translate('nav.link.forAdvertiser')}
      </BlackNavigationLink>
      <BlackNavigationLink
        to={PAGES_INFO.advertiserLanding.path}
        className={'mt-4 text-white font-core'}
      >
        {translate('nav.link.adpackages')}
      </BlackNavigationLink>
      <BlackNavigationLink
        to={PAGES_INFO.planning.path}
        className={'mt-4 text-white font-core'}
      >
        {translate('nav.link.planning')}
      </BlackNavigationLink>
      <BlackNavigationLink
        to={PAGES_INFO.newAdvertiser.path}
        className={'mt-4 text-white font-core'}
      >
        {translate('nav.link.forNewAdvertiser')}
      </BlackNavigationLink>
      <BlackNavigationLink
        to={PAGES_INFO.localAdvertising.path}
        className={'mt-4 text-white font-core'}
      >
        {translate('nav.link.localAdvertising')}
      </BlackNavigationLink>
      <BlackNavigationLink
        to={PAGES_INFO.channels.path}
        className={'mt-4 text-white font-core'}
      >
        {translate('nav.link.channels')}
      </BlackNavigationLink>
      <BlackNavigationLink
        to={PAGES_INFO.references.path}
        className={'mt-4 text-white font-core'}
      >
        {translate('nav.link.references')}
      </BlackNavigationLink>
      <BlackNavigationLink
        to={PAGES_INFO.showRoom.path}
        className={'mt-12 text-white font-extra-bold leading-loose'}
      >
        {translate('footer.link.solutions')}
      </BlackNavigationLink>
    </div>
  );
};

const FooterInfoCol3 = ({ translate, campaigns }) => {
  return (
    <div className={infoColStyle}>
      <BlackNavigationLink
        to={
          campaigns.length > 0
            ? PAGES_INFO.campaigns.path
            : PAGES_INFO.articles.path
        }
        className={'text-white font-extra-bold leading-loose'}
      >
        {translate('nav.link.current')}
      </BlackNavigationLink>

      {campaigns.length > 0 ? (
        <>
          <BlackNavigationLink
            to={PAGES_INFO.articles.path}
            className={'mt-4 text-white font-core'}
          >
            {translate('nav.link.articles')}
          </BlackNavigationLink>
          <BlackNavigationLink
            to={PAGES_INFO.campaigns.path}
            className={'mt-4 text-white font-core'}
          >
            {translate('nav.link.campaigns')}
          </BlackNavigationLink>
        </>
      ) : null}
     <BlackNavigationLink
        to={PAGES_INFO.mediaInfo.path}
        className={'mt-12 text-white font-extra-bold leading-loose'}
      >
        {translate('nav.link.mediaInfo')}
      </BlackNavigationLink>
      <BlackNavigationLink
        to={MEDIA_INFO.totalTv.path}
        className={'mt-4 text-white'}
      >
        {translate('nav.link.totalTVPrices')}
      </BlackNavigationLink>
      <BlackNavigationLink
        to={MEDIA_INFO.onlineAdvertising.path}
        className={'mt-4 text-white'}
      >
        {translate('nav.link.onlineAdvertisingPrices')}
      </BlackNavigationLink>
      <BlackNavigationLink
        to={MEDIA_INFO.material.path}
        className={'mt-4 text-white'}
      >
        {translate('nav.link.material')}
      </BlackNavigationLink>
      <BlackNavigationLink
        to={MEDIA_INFO.salesConditions.path}
        className={'mt-4 text-white'}
      >
        {translate('nav.link.salesConditions')}
      </BlackNavigationLink>
    </div>
  );
};

const FooterInfoCol4 = ({ translate }) => {
  return (
    <div className={infoColStyle}>
      <BlackNavigationLink
        to={PAGES_INFO.contact.path}
        className={'text-white font-extra-bold leading-loose'}
      >
        {translate('nav.link.contacts')}
      </BlackNavigationLink>
      <BlackNavigationLink
        to={`${TEMPLATES_INFO.brieflyInEnglish.path}advertising`}
        className={'mt-12 text-white font-extra-bold leading-loose'}
      >
        {translate('nav.link.brieflyInEnglish')}
      </BlackNavigationLink>

      <BlackNavigationLink
        to={`${TEMPLATES_INFO.brieflyInEnglish.path}advertising`}
        className={'mt-4 text-white'}
      >
        {translate('nav.link.advertisingInEnglish')}
      </BlackNavigationLink>

      <BlackNavigationLink
        to={`${TEMPLATES_INFO.brieflyInEnglish.path}programs`}
        className={'mt-4 text-white'}
      >
        {translate('nav.link.programsInEnglish')}
      </BlackNavigationLink>

      <BlackNavigationLink
        to={`${TEMPLATES_INFO.brieflyInEnglish.path}delivery-of-materials`}
        className={'mt-4 text-white'}
      >
        {translate('nav.link.materialDeliveryInEnglish')}
      </BlackNavigationLink>

      <BlackNavigationLink
        to={`${TEMPLATES_INFO.brieflyInEnglish.path}media-information`}
        className={'mt-4 text-white'}
      >
        {translate('nav.link.mediaInformationInEnglish')}
      </BlackNavigationLink>

      <BlackNavigationLink
        to={`${TEMPLATES_INFO.brieflyInEnglish.path}contact-us`}
        className={'mt-4 text-white'}
      >
        {translate('nav.link.contactInEnglish')}
      </BlackNavigationLink>
    </div>
  );
};


const FooterCompanyInfoRow = ({ translate }) => {
  const linkStyle = 'mb-4 lg:mb:0 px-6 text-black';
  return (
    <div className="my-6 mb-12 flex flex-col">
      <div className="mx-auto flex flex-row flex-wrap">
        <span
          onClick={()=>openCookieConsent()}
          className={`${ExternalLinkClassNames} ${linkStyle}`}
        >
          {translate('footer.link.cookieSettings')}
        </span>
        <ExternalLink
          to={'http://www.mtvuutiset.fi/yritys/kayttoehdot/'}
          className={linkStyle}
        >
          {translate('footer.link.termsOfUser')}
        </ExternalLink>
        <BlackNavigationLink
          to={`${TEMPLATES_INFO.article.path}mtv-oy-n-tietosuojakaytanto-mainostaja-ja-yritysasiakkaille`}
          className={linkStyle}
        >
          {translate('footer.link.dataProtectionConvention')}
        </BlackNavigationLink>
        <ExternalLink
          to={'http://www.mtvuutiset.fi/yritys/'}
          className={linkStyle}
        >
          {translate('footer.link.aboutCompany')}
        </ExternalLink>
        <ExternalLink
          to={'http://www.mtvuutiset.fi/yritys/en/'}
          className={linkStyle}
        >
          {translate('footer.link.aboutMtv')}
        </ExternalLink>
        <ExternalLink
          to={'https://varumarke.tv4.se/d/86Fbz8eu3uJt/mtv-style-guide'}
          className={linkStyle}
        >
          {translate('footer.link.styleguide')}
        </ExternalLink>
        <ExternalLink
          to={'https://www.mtvpressi.fi/'}
          className={linkStyle}
        >
          {translate('footer.link.for-media')}
        </ExternalLink>
      </div>
      <div className="mx-auto flex flex-row flex-wrap">

        <ExternalLink
          to={'https://www.mtv.fi/'}
          className={linkStyle}
        >
          {translate('footer.link.mtv-service')}
        </ExternalLink>

        <ExternalLink
          to={'https://www.mtv.fi/ota-yhteytta'}
          className={linkStyle}
        >
          {translate('footer.link.mtv-customer-service')}
        </ExternalLink>

        <ExternalLink
          to={'https://www.mtvuutiset.fi/'}
          className={linkStyle}
        >
          {translate('footer.link.mtv-news')}
        </ExternalLink>

        <ExternalLink
          to={'https://www.mtv.fi/sarja/suomiareena-33001006'}
          className={linkStyle}
        >
          {translate('footer.link.suomiareena')}
        </ExternalLink>

        <ExternalLink
          to={'https://www.mtvuutiset.fi/makuja'}
          className={linkStyle}
        >
          {translate('footer.link.makuja')}
        </ExternalLink>

        <ExternalLink
          to={'https://www.mtvuutiset.fi/palaute'}
          className={linkStyle}
        >
          {translate('footer.link.mtv-feedback')}
        </ExternalLink>

        <BodyTextS className={linkStyle}>
          {translate('footer.text.copywright')}
        </BodyTextS>
      </div>
    </div>
  );
};

export default Footer;
